type UpdateReadyCallback = () => void;

// Module-level guard — guarantees a single registration even if React calls this
// multiple times (StrictMode double-mount, re-renders, etc.)
let registered = false;

/**
 * Register the service worker and wire up update detection.
 * - Calls onUpdateReady when a new version has installed and is waiting.
 * - The page can then call applyUpdate() to swap to the new SW.
 */
export function registerServiceWorker(onUpdateReady: UpdateReadyCallback): void {
  if (registered) return;
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  const env = import.meta.env.VITE_APP_ENV;
  if (env !== 'production' && env !== 'preview') return;

  registered = true;

  // If the page has already loaded, register immediately.
  // Otherwise wait for the load event so we don't compete with critical resources.
  if (document.readyState === 'complete') {
    register(onUpdateReady);
  } else {
    window.addEventListener('load', () => register(onUpdateReady), { once: true });
  }
}

function register(onUpdateReady: UpdateReadyCallback): void {
  void navigator.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then((registration) => {
      // A waiting worker already exists from a previous tab — surface immediately.
      if (registration.waiting) {
        onUpdateReady();
      }

      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (
            installing.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            onUpdateReady();
          }
        });
      });
    })
    .catch(() => {
      // Silent fail — SW registration shouldn't break the app.
    });

  // Reload once the new SW has taken control.
  let refreshing = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });
}

/** Tell the waiting worker to activate immediately. */
export function applyUpdate(): void {
  void navigator.serviceWorker.getRegistration().then((registration) => {
    registration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
  });
}
